import { Link } from "react-router-dom";
import { Sparkles, ListChecks } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SubjectIcon } from "@/components/SubjectCard";
import { DIFFICULTIES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const go = (user, path) => (user ? path : "/register");

const TopicRow = ({ s, t, i, user }) => (
  <li data-testid={`domain-topic-${i}`} className="rounded-xl border border-line bg-canvas-2/50 p-4">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <p className="font-semibold text-ink">{t.name}</p>
      <div className="flex gap-3 text-xs font-semibold">
        <Link data-testid={`domain-topic-explain-${i}`} to={go(user, `/learn?subject=${s.id}&topic=${encodeURIComponent(t.name)}`)} className="inline-flex items-center gap-1 text-aqua hover:underline"><Sparkles className="h-3.5 w-3.5" />Explain</Link>
        <Link data-testid={`domain-topic-quiz-${i}`} to={go(user, `/quiz?subject=${s.id}&topic=${encodeURIComponent(t.name)}`)} className="inline-flex items-center gap-1 text-aqua hover:underline"><ListChecks className="h-3.5 w-3.5" />Quiz</Link>
      </div>
    </div>
    {t.subtopics?.length > 0 && <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-2">{t.subtopics.map((x) => <li key={x} className="flex items-center gap-1.5"><span className="h-1 w-1 rounded-full bg-aqua-solid" />{x}</li>)}</ul>}
  </li>
);

export default function DomainDialog({ subject: s, onClose }) {
  const { user } = useAuth();
  return (
    <Dialog open={!!s} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="domain-dialog" className="max-h-[88vh] w-[calc(100%-2rem)] max-w-2xl overflow-y-auto rounded-2xl">
        {s && <>
          <DialogHeader>
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-aqua-soft text-aqua"><SubjectIcon name={s.icon} /></span>
              <div className="text-left"><p className="font-mono text-[11px] tracking-wider text-ink-3">{s.short}</p><DialogTitle data-testid="domain-dialog-title" className="text-xl font-extrabold">{s.name}</DialogTitle></div>
            </div>
            <DialogDescription className="pt-2 text-left leading-relaxed text-ink-2">{s.description}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-ink-3">Levels:</span>
            {DIFFICULTIES.map((d) => <span key={d} className="rounded-full border border-line px-2.5 py-1 capitalize text-ink-2">{d}</span>)}
          </div>
          <ul className="flex flex-col gap-2.5">{s.topics.map((t, i) => <TopicRow key={t.id} s={s} t={t} i={i} user={user} />)}</ul>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button asChild className="rounded-full"><Link data-testid="domain-explain-btn" to={go(user, `/learn?subject=${s.id}`)}><Sparkles className="mr-1.5 h-4 w-4" />AI explanations</Link></Button>
            <Button asChild variant="outline" className="rounded-full"><Link data-testid="domain-quiz-btn" to={go(user, `/quiz?subject=${s.id}`)}><ListChecks className="mr-1.5 h-4 w-4" />Practice quiz</Link></Button>
          </div>
          {!user && <p className="text-xs text-ink-3">Create a free account to use AI explanations and quizzes. Some domains and levels need a paid plan.</p>}
        </>}
      </DialogContent>
    </Dialog>
  );
}
